import { HttpClient, HttpClientModule, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, finalize, iif, map, tap } from 'rxjs';
import { API_BASE_URL } from 'src/app/constants';

export enum NomenclatureType {
  Countries = 'Countries',
  States = 'States',
  Cities = 'Cities',
  Kids = 'Kids',
  Worker = 'Worker',
  NKPDs = 'NKPDs',
  Categories = 'Categories',
  OperationStatuses = 'OperationStatuses',
  AccountManagers = 'AccountManagers',
  ContactPersons = 'ContactPersons',
  MedikoOffices = 'MedikoOffices',
  MedikoClients = 'MedikoClients',
  RequestTypes = 'RequestTypes',
  RequestDocuments = 'RequestDocuments',
  Employee = 'Employee'
}

export interface SearchConfig {
  'Search.Operator'?: string;
  'Search.Values'?: any[];
  'Parent.Operator'?: string;
  'Parent.Values'?: any[];
  'Contact.Operator'?: string;
  'Contact.Values'?: any[];
  'Type.Operator'?: string;
  'Type.Values'?: any[]; 
  NomenclatureType: NomenclatureType;
  PageNumber?: number;
  PageSize?: number;
}

export enum SearchOperation {
  LIKE = 'Like',
  EQUAL = 'Equal',
}

@Injectable({
  providedIn: 'root',
})
export class AutocompleteQueryService extends BehaviorSubject<Array<any>> {
  private queryUrl!: string;
  public loading = false;

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private baseUrl: string
  ) {
    super([]);
    this.queryUrl = `${this.baseUrl}/GetSearchResults`;
  }

  public manualSearch(
    propValue: string | number | string[] | number[],
    propName: NomenclatureType | keyof typeof NomenclatureType,
    operation: SearchOperation = SearchOperation.EQUAL,
    parentPropName: 'Parent' | 'Contact' | 'Type' = 'Parent',
    parentPropValue?: string | number | string[] | number[],
    showCode = false,
    isComplexType = false,
    complexTypeValueNormalizer?: (data: any) => string
  ) {
    this.loading = true;
    
    const searchConfig: SearchConfig = {
      NomenclatureType: NomenclatureType[propName],
      PageNumber: 1,
      PageSize: 200
    };

    if (operation && propValue) {
      searchConfig['Search.Operator'] = operation;
      searchConfig['Search.Values'] = propValue instanceof Array ? propValue : this.tryTransformToArray(propValue);
    }
    
    switch (parentPropName) {
      case 'Parent':
        if (parentPropValue) {
          searchConfig['Parent.Operator'] = 'Equal';
          searchConfig['Parent.Values'] =
            parentPropValue instanceof Array ? parentPropValue : [parentPropValue];
        }
        break;

      case 'Contact':
        if (parentPropValue) {
          searchConfig['Contact.Operator'] = 'Equal';
          searchConfig['Contact.Values'] =
            parentPropValue instanceof Array ? parentPropValue : [parentPropValue];
        }
        break;

      case 'Type':
        if (parentPropValue) {
          searchConfig['Type.Operator'] = 'Equal';
          searchConfig['Type.Values'] =
            parentPropValue instanceof Array ? parentPropValue : [parentPropValue];
        }
        break;
    }

    const params = new HttpParams({
      fromObject: searchConfig as any,
    });

    console.log(this.queryUrl);

    return this.http
      .get<any>(`${this.baseUrl}/GetSearchResults`, { params })
      .pipe(
        finalize(() => (this.loading = false)),
        map((data) => data.info),
        map((data) =>
          data.map((entry: any) => {
            if (showCode) {
              entry.name = `${entry.code} - ${entry.name}`;
            } else if (isComplexType && complexTypeValueNormalizer) {
              entry.name = complexTypeValueNormalizer(entry);
            } 
            return entry;
          })
        ),
        map((result) => {
          super.next(result);
          if (operation === SearchOperation.EQUAL && result.length > 0) {
            return result[0];
          }
          return result;
        })
      );
  }

  public search(
    propValue: string | number | string[] | number[],
    propName: NomenclatureType | keyof typeof NomenclatureType,
    parentPropName: 'Parent' | 'Contact' | 'Type' = 'Parent',
    parentPropValue?: string | number | string[] | number[],
    showCode = false,
    exact?: boolean,
    isComplexType = false,
    complexTypeValueNormalizer?: (data: any) => string 
  ) {
    this.loading = true;

    const searchConfig: SearchConfig = {
      NomenclatureType: NomenclatureType[propName],
      PageNumber: 1,
      PageSize: 200
    };

    searchConfig['Search.Operator'] = exact  ? 'Equal' : 'Like';
    searchConfig['Search.Values'] = propValue instanceof Array ? propValue : this.tryTransformToArray(propValue);

    switch (parentPropName) {
      case 'Parent':
        if (parentPropValue) {
          searchConfig['Parent.Operator'] = 'Equal';
          searchConfig['Parent.Values'] =
            parentPropValue instanceof Array ? parentPropValue : [parentPropValue];
        }
        break;

      case 'Contact':
        if (parentPropValue) {
          searchConfig['Contact.Operator'] = 'Equal';
          searchConfig['Contact.Values'] =
            parentPropValue instanceof Array ? parentPropValue : [parentPropValue];
        }
        break;

      case 'Type':
        if (parentPropValue) {
          searchConfig['Type.Operator'] = 'Equal';
          searchConfig['Type.Values'] =
            parentPropValue instanceof Array ? parentPropValue : [parentPropValue];
        }
        break;
    }

    const params = new HttpParams({
      fromObject: searchConfig as any,
    });

    // TODO: Figure return types
    return this.http
      .get<any>(this.queryUrl, { params })
      .pipe(
        finalize(() => (this.loading = false)),
        map((data) => data.info),
        map((data) =>
          data.map((entry: any) => {
            if (showCode) {
              entry.name = `${entry.code} - ${entry.name}`;
            } else if (isComplexType && complexTypeValueNormalizer) {
              entry.name = complexTypeValueNormalizer(entry);
            } 
            return entry;
          })
        )
      )
      .subscribe((data) => super.next(data));
  }

  private tryTransformToArray(value: string | number ): string[] | number[] {
    if (typeof value === 'number') return [value];
    const splittedParts = value.split(/[,.\s]/);
    if (splittedParts.length == 0) return [value];
    return splittedParts;
  }
}
